"use client";
import useToastNotification from "@/hook/useToastNotification";
import { submitEnquiryForm } from "@/store/feature/enquiry/enquiryFormApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { isValidPhoneNumber } from "libphonenumber-js";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { qualificationOptions } from "./Helper";
import { allowedDomains } from "@/utils/Constants";
import { LoadingIcon } from "./Icons";
import { useAuth } from "@/hook/useAuth";
import Link from "next/link";

const EnquiryForm = () => {
  const { isAnonymous } = useAuth();
  const dispatch = useAppDispatch();
  const { countryList } = useAppSelector((state) => state.country);
  const { showToast } = useToastNotification();
  const searchParams = useSearchParams();
  const platform = searchParams.get("platform");
  const router = useRouter();

  const countryImage = (name: string) =>
    `/assets/images/png/${name.replace(/\s+/g, "-")}.png`;

  const [loading, setLoading] = useState(false);

  const [formDetails, setFormDetails] = useState<{
    fullName: string;
    phoneNumber: string;
    email: string;
    qualification: string;
    message: string;
    country: string[];
    terms: boolean;
  }>({
    fullName: "",
    phoneNumber: "",
    email: "",
    qualification: "",
    message: "",
    country: [],
    terms: false,
  });
  const [showAllCountries, setShowAllCountries] = useState(false);

  const [error, setError] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    terms: "",
  });

  const isFirstRender = useRef(true);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    isFirstRender.current = false;
    setHydrated(true);
  }, []);

  // CHECK IF EMAIL IS VALID OR NOT
  const isAllowedEmail = (email: string): boolean => {
    const domain = email.split("@")[1]?.toLowerCase();
    return domain ? allowedDomains.includes(domain) : false;
  };

  const onSubmitHandler = async () => {
    const isPhoneValid = isValidPhoneNumber(formDetails.phoneNumber || "");

    const hasErrors = {
      fullName: !formDetails.fullName.trim() ? "Please enter a valid name" : "",
      phoneNumber: !isPhoneValid ? "Please enter a valid phone number" : "",
      email: !formDetails.email.trim() ? "Please enter a valid email" : "",
      terms: !formDetails.terms
        ? "Please accept the terms and conditions."
        : "",
    };

    setError(hasErrors);
    const anyErrors = Object.values(hasErrors).some(Boolean);

    if (!platform) {
      showToast("Invalid URL", "error");
      return;
    }

    // Check for special characters in full name
    if (
      /[^a-zA-Z\s]/.test(formDetails.fullName) || // special chars or numbers
      /^\s|\s$/.test(formDetails.fullName) || // leading or trailing space
      / {2,}/.test(formDetails.fullName) // more than 2 consecutive spaces
    ) {
      showToast(
        "Full name should not contain special characters or numbers or consecutive spaces.",
        "error",
      );
      setError((prev) => ({
        ...prev,
        fullName:
          "Full name should not contain special characters or numbers or consecutive spaces.",
      }));
      return;
    }
    if (!formDetails.terms) {
      showToast("Please accept the terms and conditions.", "error");
      setError((prev) => ({
        ...prev,
        terms: "Please accept the terms and conditions.",
      }));
      return;
    }

    if (!isAllowedEmail(formDetails.email)) {
      showToast(
        "Please use an email address from Gmail, Outlook, Microsoft, Hotmail, or ProtonMail.",
        "error",
      );
      setError((prev) => ({
        ...prev,
        email:
          "Please use an email address from Gmail, Outlook, Microsoft, Hotmail, or ProtonMail.",
      }));
      return;
    }

    if (anyErrors) {
      showToast("Please fill in all the required fields.", "error");
      return;
    } else {
      setLoading(true);
      const { payload: response } = await dispatch(
        submitEnquiryForm({
          fullName: formDetails.fullName,
          phoneNumber: formDetails.phoneNumber,
          email: formDetails.email,
          highestQualification: formDetails.qualification,
          message: formDetails.message,
          interestedCountries: formDetails.country,
          platform: platform.toLowerCase() || "",
          analyticsFormName: "student_enquiry_form",
        }),
      );
      const userDetails = {
        fullName: formDetails.fullName,
        phoneNumber: formDetails.phoneNumber,
        email: formDetails.email,
      };
      if (isAnonymous) {
        localStorage.setItem("userDetails", JSON.stringify(userDetails));
      }
      if (
        response &&
        typeof response === "object" &&
        "enquiryId" in response &&
        typeof (response as { enquiryId?: string }).enquiryId === "string"
      ) {
        setLoading(false);
        showToast(
          (response as { message?: string }).message ||
            "Enquiry submitted successfully.",
          "success",
        );
        if (isAnonymous) {
          router.push("/auth");
        } else {
          router.push("/dashboard");
        }
        setFormDetails({
          fullName: "",
          phoneNumber: "",
          email: "",
          qualification: "",
          message: "",
          country: [],
          terms: false,
        });
      } else {
        showToast("Failed to submit enquiry. Please try again.", "error");
        setLoading(false);
      }
    }
  };

  return (
    <div className="max-w-[735px] mx-auto">
      {/* Full Name */}
      <div className="flex flex-col relative">
        <span className="absolute -bottom-5 text-red-500 text-xs sm:text-sm ff_poppins">
          {error.fullName}
        </span>
        <label
          htmlFor="FullName"
          className="ff_poppins text-sm sm:text-base lg:text-lg xl:text-xl text-[#586166] font-medium"
        >
          Full name (As per official documents)
          <span className="text-[#F21D2F]">*</span>
        </label>
        <input
          value={formDetails.fullName}
          onChange={(e) => {
            setFormDetails({ ...formDetails, fullName: e.target.value });
            setError({ ...error, fullName: "" });
          }}
          required
          id="FullName"
          type="text"
          placeholder="Enter your full name"
          className={`placeholder:text-[#8D9396] border-2 text-[#586166] text-sm xl:text-lg mt-2 bg-white !p-[11px] 3xl:!p-4 rounded-[6px] font-medium duration-300 ease-in-out ${
            error.fullName ? "border-[#F21D2F]" : "border-transparent"
          }`}
        />
      </div>

      {/* Phone Number */}
      <div className="flex flex-col mt-[32px] relative">
        <span className="absolute -bottom-5 text-red-500 text-xs sm:text-sm ff_poppins">
          {error.phoneNumber}
        </span>
        <label
          htmlFor="phone"
          className="ff_poppins text-sm sm:text-base lg:text-lg xl:text-xl text-[#586166] font-medium"
        >
          Mobile Number (So we can reach out to help)
          <span className="text-[#F21D2F]">*</span>
        </label>
        <div
          className={`bg-white rounded-[6px] border-2 py-1 mt-2 ${
            error.phoneNumber ? "border-[#F21D2F]" : ""
          }`}
        >
          {hydrated && (
            <PhoneInput
              className="w-full rounded-[8px] h-[35px] xl:!h-[45px] 3xl:!h-[54px]"
              defaultCountry="in"
              forceDialCode
              hideDropdown={true}
              inputClassName="!h-[35px] !text-lg 3xl:!text-xl xl:!h-[45px] 3xl:!h-[54px]"
              value={formDetails.phoneNumber}
              onChange={(value) => {
                if (isFirstRender.current) return;
                setFormDetails((prev) => ({ ...prev, phoneNumber: value }));
                setError((prev) => ({ ...prev, phoneNumber: "" }));
              }}
              inputProps={{
                onPaste: (e) => {
                  e.preventDefault();
                },
                onCopy: (e) => e.preventDefault(),
                onCut: (e) => e.preventDefault(),
              }}
            />
          )}
        </div>
      </div>

      {/* Email */}
      <div className="flex flex-col mt-[32px] relative">
        <span className="absolute bottom-[-32px] sm:-bottom-5 text-red-500 text-xs sm:text-sm ff_poppins">
          {error.email}
        </span>
        <label
          htmlFor="email"
          className="ff_poppins text-sm sm:text-base lg:text-lg xl:text-xl text-[#586166] font-medium"
        >
          Email Address (Updates will land here)
          <span className="text-[#F21D2F]">*</span>
        </label>
        <input
          value={formDetails.email}
          onChange={(e) => {
            setFormDetails({ ...formDetails, email: e.target.value });
            setError({ ...error, email: "" });
          }}
          required
          id="email"
          type="email"
          placeholder="Enter your email"
          className={`placeholder:text-[#8D9396] text-[#586166] border-2 text-sm xl:text-lg mt-2 bg-white !p-[11px] 3xl:!p-4 rounded-[6px] font-medium ${
            error.email ? "border-[#F21D2F]" : "border-transparent"
          }`}
        />
      </div>

      {/* Qualification */}
      <div className="flex flex-col mt-[32px]">
        <label className="ff_poppins text-sm sm:text-base lg:text-lg xl:text-xl text-[#586166] font-medium">
          Last Qualification (Your recent educational degree)
        </label>
        <div className="mt-2">
          <Select
            value={formDetails.qualification}
            onValueChange={(value) =>
              setFormDetails({ ...formDetails, qualification: value })
            }
          >
            <SelectTrigger className="w-full !p-[11px] 3xl:!p-4 bg-white rounded-[6px] text-[#586166] text-sm sm:text-lg font-medium ff_poppins">
              <SelectValue placeholder="Select Last Qualification" />
            </SelectTrigger>
            <SelectContent className="bg-white shadow-2xl">
              <SelectGroup>
                {qualificationOptions.map((item) =>
                  item.title === "10th" ? null : (
                    <SelectItem
                      key={item.id}
                      value={item.title}
                      className="hover:bg-gray-200 duration-300 ease-in-out text-sm sm:text-lg font-medium text-[#586166] ff_poppins"
                    >
                      {item.title}
                    </SelectItem>
                  ),
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Message */}
      <div className="flex flex-col mt-[32px]">
        <label
          htmlFor="message"
          className="ff_poppins text-sm sm:text-base lg:text-lg xl:text-xl text-[#586166] font-medium"
        >
          Message For Us
        </label>
        <textarea
          value={formDetails.message}
          onChange={(e) => {
            const words = e.target.value.trim().split(/\s+/);
            if (words.length <= 100) {
              setFormDetails({ ...formDetails, message: e.target.value });
            } else {
              setFormDetails({
                ...formDetails,
                message: words.slice(0, 100).join(" "),
              });
            }
          }}
          id="message"
          placeholder="Share your query, goal, or anything you'd like us to know."
          className="placeholder:text-[#8D9396] text-[#586166] text-sm xl:text-lg mt-2 bg-white !p-[11px] 3xl:!p-4 rounded-[6px] font-medium"
        />
        <span className="text-sm text-gray-500 mt-1 ff_poppins">
          {formDetails.message.trim() === ""
            ? 0
            : formDetails.message.trim().split(/\s+/).length}
          /100 words
        </span>
      </div>
      <div className="flex items-center gap-3 ff_poppins my-4 w-full">
        <Checkbox
          required
          className="cursor-pointer"
          value={formDetails.terms ? "checked" : "unchecked"}
          onCheckedChange={(checked) => {
            setFormDetails((prev) => ({
              ...prev,
              terms: checked === true,
            }));
          }}
        />
        <p
          className={`ff_poppins text-sm font-normal  ${!error.terms ? "text-black" : " text-red-500"}`}
        >
          I hereby accept the{" "}
          <Link
            target="_blank"
            href={"/policies/terms-conditions"}
            className="text-[#3777FF] underline"
          >
            terms and conditions
          </Link>{" "}
          of the platform and agree to abide by its{" "}
          <Link
            target="_blank"
            href={"/policies/privacy-policy"}
            className="text-[#3777FF] underline"
          >
            policies and guidelines.
          </Link>
        </p>
      </div>
      {/* Country Selection */}
      {countryList.length > 0 && (
        <div className="flex flex-col mt-[24px]">
          <p className="ff_poppins font-medium text-sm sm:text-base lg:text-lg xl:text-xl text-[#586166] mb-4">
            Select Interested Countries
          </p>

          <div className="flex items-center gap-[10px] sm:gap-[12px] flex-wrap">
            {(showAllCountries ? countryList : countryList.slice(0, 5)).map(
              (country) => {
                const countryName = country.country_name.replace(
                  "europe - ",
                  "",
                );
                const isSelected = formDetails.country.includes(countryName);
                const image = countryImage(countryName);

                return (
                  <div
                    key={country.country_id}
                    className={`border-[2px] sm:border-[3px] border-[#EB9757] rounded-full flex items-center py-1 sm:py-2.5 px-3 sm:px-5 gap-2 cursor-pointer transition-all duration-200 ${
                      isSelected ? "bg-[#EB9757]/10" : "bg-white hover:bg-slate-50"
                    }`}
                    onClick={() => {
                      setFormDetails((prev) => {
                        const updatedCountries = prev.country.includes(
                          countryName,
                        )
                          ? prev.country.filter((c) => c !== countryName)
                          : [...prev.country, countryName];

                        return {
                          ...prev,
                          country: updatedCountries,
                        };
                      });
                    }}
                  >
                    <Checkbox
                      className="rounded-full pointer-events-none size-3 sm:size-4"
                      checked={isSelected}
                      onCheckedChange={() => {}}
                    />
                    <p className="text-xs sm:text-sm lg:text-base font-medium ff_poppins capitalize">
                      {countryName}
                    </p>
                    <Image
                      className="max-w-[20px] sm:max-w-[25px] w-full sm:max-h-[35px] object-cover rounded-[2px]"
                      src={image}
                      alt="logo"
                      width={35}
                      height={24}
                    />
                  </div>
                );
              },
            )}
          </div>

          {countryList.length > 5 && (
            <button
              type="button"
              onClick={() => setShowAllCountries(!showAllCountries)}
              className="ff_poppins font-medium text-xs sm:text-sm text-[#EB9757] underline cursor-pointer mt-3.5 inline-block self-start"
            >
              {showAllCountries
                ? "Show less"
                : `View ${countryList.slice(5).length} more countries`}
            </button>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-5 sm:gap-7 lg:gap-9  xl:gap-10 mt-[35px] sm:mt-[45px] md:mt-[55px] lg:mt-[65px] xl:mt-[75px] 2xl:mt-[85px] 3xl:mt-[94px] justify-start">
        <button
          data-ga-form="student_enquiry_form"
          onClick={onSubmitHandler}
          type="submit"
          className="cursor-pointer font-semibold ff_poppins text-white text-sm sm:text-lg md:text-xl xl:text-2xl py-[14px] px-[40px] xl:px-[67px] sm:py-4 lg:py-[19.5px] rounded-xl gradient_button_shadow bg-button-gradient text-center hover:scale-105 duration-300 ease-in-out"
        >
          {loading ? <LoadingIcon /> : `Send Enquiry`}
        </button>
      </div>
    </div>
  );
};

export default EnquiryForm;
